// Single source of truth for the footer/stepper viewport arithmetic.
// These values are geometrically coupled across useFooterMode (fixed vs
// flow decision), Copyrighttext, Footer, MenteeCreateProfileStepper and
// Background — change them here, never inline.

export const COPYRIGHT_FOOTER_HEIGHT = 28; // Copyrighttext bar

// Band tolerance used by useFooterMode: scrollHeight/clientHeight can
// disagree by a couple px under zoom/display scaling. The panel budgets
// below are sized against this value — keep them in step.
export const FOOTER_BAND_TOLERANCE = 4;
export const DASHBOARD_FOOTER_HEIGHT = 28; // DashboardFooter bar

// Flow-mode gap between the page content and the copyright bar; hosts the
// onboarding stepper pills at full scroll.
export const FOOTER_FLOW_GAP = 70;

export const STEPPER_PILL_HEIGHT = 10;
export const STEPPER_FOOTER_GAP = 12; // gap between the pills and the bar

// Fixed mode: pills sit this far above the viewport bottom — just above
// the anchored copyright bar.
export const STEPPER_BOTTOM_OFFSET =
  COPYRIGHT_FOOTER_HEIGHT + STEPPER_FOOTER_GAP;

// Flow mode: how far the pills are lifted back over the FOOTER_FLOW_GAP
// zone to keep the same rhythm above the in-flow bar.
export const STEPPER_FLOW_LIFT = STEPPER_BOTTOM_OFFSET + STEPPER_PILL_HEIGHT;

// Dashboard tab pages: the in-flow MenteeHeader above the Background box,
// with the footer bar added on top. The trimmed header measures ~60-80px
// across breakpoints; this must exceed the tallest header by AT LEAST
// footerHeight - TOLERANCE (see useFooterMode) so the page's trailing
// edge ends below the under-bar band — inside it the footer falsely
// un-anchors and the page gains an idle scroll. Every extra px widens the
// plain gap between the map's bottom edge and the bar, so don't overshoot.
const DASHBOARD_HEADER_CHROME = 82;
export const DASHBOARD_CHROME =
  DASHBOARD_HEADER_CHROME + DASHBOARD_FOOTER_HEIGHT;

// Profile section: the sidebar and the edit-profile content panel must end
// on the same line — the panel adds ~104px of chrome (padding + Save row)
// on top of its inner scroll area, hence the pairwise offsets. The maps
// below hold the non-footer chrome per breakpoint (header, page paddings,
// panel chrome); the footer bar is added on top so the panels track its
// height automatically.
const withFooter = (chrome) =>
  Object.fromEntries(
    Object.entries(chrome).map(([breakpoint, px]) => [
      breakpoint,
      `calc(100vh - ${px + DASHBOARD_FOOTER_HEIGHT}px)`,
    ]),
  );

// lg/xl include extra room so the page's trailing edge ends clear of the
// anchored bar's overlay zone (footer + useFooterMode's TOLERANCE) — if it
// lands inside that band the footer un-anchors and the page gains an idle
// scroll.
// Bases chosen so the panels end ~10-12px above the footer bar — the same
// rhythm as the ~10px gap below the header (equidistant). The edit-profile
// pages' wrapper Box keeps its bottom padding at 6-10px so the page's
// trailing edge stays clear of useFooterMode's under-bar band.
export const PROFILE_SIDEBAR_HEIGHT = withFooter({
  xs: 205,
  sm: 84,
  md: 84,
  lg: 86,
  xl: 120,
});

export const EDIT_PROFILE_PANEL_MAX_HEIGHT = withFooter({
  xs: 210,
  sm: 188,
  md: 188,
  lg: 190,
  xl: 224,
});

// Page-wrapper padding for the four edit-profile variants. The bottom
// padding counts toward scrollHeight, so it participates in the same
// trailing-edge arithmetic as the heights above — change them together.
export const EDIT_PROFILE_PAGE_PADDING = {
  xs: "10px 0 6px",
  sm: "10px 0 6px",
  md: "10px 0 6px",
  lg: "10px 0 6px",
  xl: "20px 0 10px",
};
